/* PDP Redesign — Phase 2 behaviors
 * - Thumbnail click swaps the main stage media
 * - Quantity stepper increment/decrement
 * - Mobile sticky bar appears when the desktop CTA scrolls out of view
 *
 * No framework. Idempotent (safe to re-run on partial DOM updates).
 */
(function () {
  "use strict";

  function each(selector, fn, root) {
    (root || document).querySelectorAll(selector).forEach(fn);
  }

  // ----------- Gallery thumbnail click -----------
  function initGalleryThumbs() {
    each("[data-pdp-redesign]", function (wrap) {
      const stage = wrap.querySelector("[data-pdp-stage]");
      if (!stage) return;
      const thumbs = wrap.querySelectorAll("[data-pdp-thumb]");
      thumbs.forEach(function (thumb) {
        if (thumb.dataset.pdpBound) return;
        thumb.dataset.pdpBound = "1";
        thumb.addEventListener("click", function () {
          const mediaId = thumb.dataset.mediaId;
          stage.querySelectorAll("[data-pdp-media-id]").forEach(function (m) {
            m.style.display = m.dataset.pdpMediaId === mediaId ? "block" : "none";
          });
          thumbs.forEach(function (t) { t.setAttribute("aria-current", t === thumb ? "true" : "false"); });
        });
      });
    });
  }

  // ----------- Thumb rail scroll cues -----------
  // The native scrollbar is hidden in CSS. This keeps data-rail-prev /
  // data-rail-next on the rail wrapper in sync with what is still scrollable,
  // which drives both the edge fade and the chevron buttons. The rail is
  // vertical on desktop and horizontal under 980px, so the axis is read from
  // whichever dimension actually overflows rather than from a breakpoint.
  function initThumbRails() {
    each("[data-pdp-rail-wrap]", function (wrap) {
      const rail = wrap.querySelector("[data-pdp-rail]");
      if (!rail) return;

      function axis() {
        return rail.scrollWidth - rail.clientWidth > 1 ? "x" : "y";
      }

      function update() {
        const horizontal = axis() === "x";
        const pos = horizontal ? rail.scrollLeft : rail.scrollTop;
        const max = horizontal
          ? rail.scrollWidth - rail.clientWidth
          : rail.scrollHeight - rail.clientHeight;
        // 1px slack: fractional layout sizes otherwise leave a chevron stuck on
        wrap.dataset.railPrev = max > 1 && pos > 1 ? "1" : "0";
        wrap.dataset.railNext = max > 1 && pos < max - 1 ? "1" : "0";
      }

      function step(dir) {
        const horizontal = axis() === "x";
        // two thumbs (80px + 10px gap) per press
        const by = 180 * dir;
        rail.scrollBy(
          horizontal ? { left: by, behavior: "smooth" } : { top: by, behavior: "smooth" }
        );
      }

      if (!wrap.dataset.pdpRailBound) {
        wrap.dataset.pdpRailBound = "1";
        rail.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        const prev = wrap.querySelector("[data-pdp-rail-prev]");
        const next = wrap.querySelector("[data-pdp-rail-next]");
        if (prev) prev.addEventListener("click", function () { step(-1); });
        if (next) next.addEventListener("click", function () { step(1); });
        // Variant changes hide/show thumbs, which changes what overflows.
        // Watching `hidden` keeps this decoupled from the variant picker.
        if (window.MutationObserver) {
          new MutationObserver(function () {
            const current = rail.querySelector('[data-pdp-thumb][aria-current="true"]:not([hidden])');
            if (current && current.scrollIntoView) {
              current.scrollIntoView({ block: "nearest", inline: "nearest" });
            }
            update();
          }).observe(rail, { attributes: true, subtree: true, attributeFilter: ["hidden"] });
        }
      }

      update();
    });
  }

  // ----------- Quantity stepper -----------
  function initQtySteppers() {
    each("[data-pdp-qty]", function (wrap) {
      if (wrap.dataset.pdpBound) return;
      wrap.dataset.pdpBound = "1";
      const input = wrap.querySelector("input");
      const dec = wrap.querySelector("[data-pdp-qty-dec]");
      const inc = wrap.querySelector("[data-pdp-qty-inc]");
      if (!input) return;
      function clamp(n) {
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 999;
        return Math.max(min, Math.min(max, n));
      }
      dec && dec.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
      inc && inc.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  // ----------- Mobile sticky bar -----------
  function initMobileBar() {
    const bars = document.querySelectorAll("[data-pdp-mobile-bar]");
    const ctas = document.querySelectorAll("[data-pdp-cta]");
    if (!bars.length || !ctas.length) return;

    function setVisible(v) {
      bars.forEach(function (b) { b.dataset.visible = v ? "true" : "false"; });
    }

    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(function (entries) {
      const anyIntersecting = entries.some(function (e) { return e.isIntersecting; });
      setVisible(!anyIntersecting);
    }, { threshold: 0, rootMargin: "0px 0px -80px 0px" });

    ctas.forEach(function (cta) { io.observe(cta); });
  }

  // ----------- Scroll-reveal for editorial sections -----------
  // Adds .pdp-inview as sections enter the viewport. The hidden initial
  // state only applies under html.pdp-motion, so content is never hidden
  // if JS fails or reduced motion is preferred.
  function initReveals() {
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    document.documentElement.classList.add("pdp-motion");

    const targets = document.querySelectorAll(
      "[data-pdp-section] .pdp-section__inner, [data-pdp-section] .pdp-strip__inner, [data-pdp-section] .pdp-reviews__inner"
    );
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("pdp-inview");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0, rootMargin: "0px 0px 12% 0px" });

    const observed = [];
    targets.forEach(function (t) {
      if (t.dataset.pdpRevealBound) return;
      t.dataset.pdpRevealBound = "1";
      // Already in view on load → show immediately, no animation flash
      const r = t.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        t.classList.add("pdp-inview");
      } else {
        io.observe(t);
        observed.push(t);
      }
    });

    // Failsafe: nothing may ever stay hidden — reveal all after 4s
    if (observed.length) {
      setTimeout(function () {
        observed.forEach(function (t) { t.classList.add("pdp-inview"); io.unobserve(t); });
      }, 4000);
    }
  }

  // ----------- Variant picker (self-contained; theme's variant-radios is not used here) -----------
  function initVariantPicker() {
    each("[data-pdp-variant-root]", function (root) {
      if (root.dataset.pdpBound) return;
      root.dataset.pdpBound = "1";
      var dataEl = root.querySelector("[data-pdp-variants]");
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var idInput = document.querySelector('.pdp-buy input[name="id"]');
      var priceEl = document.querySelector(".pdp-buy__price span");
      var cta = document.querySelector("[data-pdp-cta]");
      var stage = document.querySelector("[data-pdp-stage]");
      var thumbs = document.querySelectorAll("[data-pdp-thumb]");
      var firstStage = stage && stage.querySelector("[data-pdp-media-id]");
      var defaultMediaId = firstStage ? firstStage.dataset.pdpMediaId : null;
      // preserve whatever separator the theme rendered (e.g. "Add to Bath — $X")
      var ctaPrefix = cta ? cta.textContent.replace(/[$€£][\d.,]+\s*$/, "") : "";

      function selectedValues() {
        return Array.prototype.map.call(root.querySelectorAll(".pdp-buy__option"), function (f) {
          var c = f.querySelector('input[type="radio"]:checked');
          return c ? c.value : null;
        });
      }
      function findVariant(vals) {
        return variants.find(function (v) {
          return v.options.length === vals.length && v.options.every(function (o, i) {
            return vals[i] == null || o === vals[i];
          });
        });
      }
      function setRadios(optionValues) {
        root.querySelectorAll(".pdp-buy__option").forEach(function (fs, i) {
          fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
            input.checked = input.value === optionValues[i];
          });
        });
      }
      // Sparse option matrix (e.g. Fragrance Free comes in 100 ct and 4 x 100 ct
      // only, White Musk only in the 50 ct puppy sizes). The rows cascade: the
      // first option shows every value, and each row below it is narrowed to the
      // values that exist for what is selected above it, so the sizes on screen
      // are always the ones the chosen flavor is actually sold in. Testing only
      // against the rows ABOVE is what keeps every flavor reachable — filtering
      // against the rows below too would let a size lock a flavor out.
      function markCompatibility() {
        var vals = selectedValues();
        root.querySelectorAll(".pdp-buy__option").forEach(function (fs, i) {
          fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
            var exists = variants.some(function (v) {
              if (v.options[i] !== input.value) return false;
              for (var j = 0; j < i; j++) {
                if (vals[j] != null && v.options[j] !== vals[j]) return false;
              }
              return true;
            });
            var label = root.querySelector('label[for="' + input.id + '"]');
            if (label) label.hidden = !exists;
          });
        });
      }
      function inGroup(el, variantId) {
        var g = (el.dataset.variantGroups || "").trim();
        return !g || (" " + g + " ").indexOf(" " + variantId + " ") !== -1;
      }
      // Variant-scoped gallery: show only the selected variant's media set
      // (data-variant-groups stamped by pdp-hero.liquid from lumi.variant_media_map;
      // unmapped media is shared and stays visible for every variant).
      function scopeGallery(v) {
        if (!stage) return;
        var vid = String(v.id);
        var visibleIds = [];
        stage.querySelectorAll("[data-pdp-media-id]").forEach(function (m) {
          if (inGroup(m, vid)) visibleIds.push(String(m.dataset.pdpMediaId));
        });
        var show = v.media != null && visibleIds.indexOf(String(v.media)) !== -1
          ? String(v.media)
          : (visibleIds[0] || String(defaultMediaId));
        stage.querySelectorAll("[data-pdp-media-id]").forEach(function (m) {
          m.style.display = String(m.dataset.pdpMediaId) === show ? "block" : "none";
        });
        thumbs.forEach(function (t) {
          var vis = inGroup(t, vid);
          t.hidden = !vis;
          t.setAttribute("aria-current", vis && String(t.dataset.mediaId) === show ? "true" : "false");
        });
      }
      // Variant-scoped copy: description, specs, ritual, bands and FAQ blocks
      // are rendered once per content set and stamped with data-variant-groups
      // by pdp-vscope-attrs. Same rule as the gallery: show the blocks the
      // selected variant belongs to, hide the rest.
      function scopeCopy(v) {
        var vid = String(v.id);
        document.querySelectorAll("[data-pdp-vscope]").forEach(function (el) {
          el.hidden = !inGroup(el, vid);
        });
      }
      function updateChips() {
        root.querySelectorAll(".pdp-buy__chip").forEach(function (label) {
          var input = document.getElementById(label.getAttribute("for"));
          label.setAttribute("aria-checked", input && input.checked ? "true" : "false");
        });
      }
      function apply(changedInput) {
        var v = findVariant(selectedValues());
        if (!v && changedInput) {
          // no variant for this combination — keep the option the user just
          // picked and snap the other options to the nearest existing variant
          var pos = parseInt(changedInput.dataset.optionPosition, 10) - 1;
          v = variants.find(function (x) { return x.available && x.options[pos] === changedInput.value; }) ||
              variants.find(function (x) { return x.options[pos] === changedInput.value; });
          if (v) setRadios(v.options);
        }
        updateChips();
        markCompatibility();
        if (!v) return;
        if (idInput) { idInput.value = v.id; idInput.removeAttribute("disabled"); }
        if (priceEl) priceEl.textContent = v.price;
        each("[data-pdp-bar-price]", function (el) { el.textContent = v.price; });
        if (cta) {
          if (v.available) { cta.removeAttribute("disabled"); cta.textContent = ctaPrefix + v.price; }
          else { cta.setAttribute("disabled", ""); cta.textContent = "Sold out"; }
        }
        scopeGallery(v);
        scopeCopy(v);
        try { history.replaceState({}, "", location.pathname + "?variant=" + v.id); } catch (e) {}
      }
      root.addEventListener("change", function (e) {
        var t = e.target;
        if (t && t.matches && t.matches('input[type="radio"]')) apply(t);
      });
      root.querySelectorAll(".pdp-buy__chip").forEach(function (label) {
        label.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            var input = document.getElementById(label.getAttribute("for"));
            if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event("change", { bubbles: true })); }
          }
        });
      });
      updateChips();
      markCompatibility();
    });
  }

  function init() {
    initGalleryThumbs();
    initThumbRails();
    initQtySteppers();
    initMobileBar();
    initReveals();
    initVariantPicker();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:reorder", init);
})();
